import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { GROUPS } from '../data/tournament';
import { advanceKnockoutPick } from '../utils/bracketUtils';
import GroupStage from '../components/GroupStage';
import ThirdPlacePicker from '../components/ThirdPlacePicker';
import KnockoutBracket from '../components/KnockoutBracket';

const API = '';

// EDT = UTC-4; returns a Date whose UTC fields equal the Eastern time
function getET() {
  return new Date(Date.now() - 4 * 60 * 60 * 1000);
}

function fmtCountdown(targetH, targetM) {
  const et = getET();
  const target = new Date(et);
  target.setUTCHours(targetH, targetM, 0, 0);
  if (target <= et) target.setUTCDate(target.getUTCDate() + 1);
  const diff = target - et;
  if (diff <= 0) return '00:00:00';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  // Normalize: take only YYYY-MM-DD in case pg returns a full ISO timestamp
  const datePart = String(dateStr).slice(0, 10);
  return new Date(datePart + 'T12:00:00Z').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  }) + ' ET';
}

const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };
const EMPTY_PICKS = { groups: {}, thirdPlaceAdvance: [], knockout: {} };

export default function DailySim() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const [picks, setPicks] = useState(EMPTY_PICKS);
  const [bracketTab, setBracketTab] = useState('groups');
  const [joining, setJoining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [joinMsg, setJoinMsg] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [savedOnce, setSavedOnce] = useState(false);
  const [picksChanged, setPicksChanged] = useState(false);

  const [showHistory, setShowHistory] = useState(false);
  const [tick, setTick] = useState(0); // forces countdown re-render

  // Track initial picks to detect changes
  const initialPicksRef = useRef(null);
  const isDirtyRef = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/daily-sim/today`, { headers });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setData(d);

      // Load user's picks into editor if they have an entry
      if (d.userEntry?.picks) {
        const p = d.userEntry.picks;
        // Don't overwrite unsaved local changes during background polls
        if (!isDirtyRef.current) {
          setPicks(p);
          initialPicksRef.current = JSON.stringify(p);
          setPicksChanged(false);
        }
        setSavedOnce(true);
      }
      setLoading(false);
    } catch {
      setLoadErr('Failed to load simulation data.');
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/daily-sim/history`);
      if (res.ok) setHistory(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchData(); fetchHistory(); }, [fetchData, fetchHistory]);

  // Poll while not complete
  useEffect(() => {
    if (data?.session?.status === 'complete') return;
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [data?.session?.status, fetchData]);

  // Countdown ticker
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Picks state management ──────────────────────────────────────────────────

  const changePicks = useCallback((updater) => {
    setPicks(prev => {
      const next = updater(prev);
      const dirty = JSON.stringify(next) !== initialPicksRef.current;
      isDirtyRef.current = dirty;
      setPicksChanged(dirty);
      setSaveMsg('');
      return next;
    });
  }, []);

  const updateGroupOrder = useCallback((group, orderedIds) => {
    changePicks(p => ({ ...p, groups: { ...p.groups, [group]: orderedIds } }));
  }, [changePicks]);

  const toggleThirdPlace = useCallback((groupLetter) => {
    changePicks(p => {
      const cur = p.thirdPlaceAdvance || [];
      if (cur.includes(groupLetter)) return { ...p, thirdPlaceAdvance: cur.filter(g => g !== groupLetter) };
      if (cur.length >= 8) return p;
      return { ...p, thirdPlaceAdvance: [...cur, groupLetter] };
    });
  }, [changePicks]);

  const updateKnockout = useCallback((matchId, teamId) => {
    changePicks(p => ({ ...p, knockout: advanceKnockoutPick(p.knockout || {}, matchId, teamId) }));
  }, [changePicks]);

  // Build teams helpers from data (null-safe; recalculated when data changes)
  const teamsArray = data ? Object.values(data.teamsById) : [];
  const teamsByGroupFn = useCallback((g) => teamsArray.filter(t => t.group_name === g), [data?.teamsById]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const join = async () => {
    setJoining(true);
    setJoinMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/daily-sim/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) { setJoinMsg(d.error); return; }

      // Pre-fill editor with the auto-generated picks from the server
      if (d.picks) {
        setPicks(d.picks);
        initialPicksRef.current = JSON.stringify(d.picks);
        setSavedOnce(true); // server already persisted the random picks
        setPicksChanged(false);
      }
      setJoinMsg(d.message);
      fetchData();
    } catch {
      setJoinMsg('Network error. Try again.');
    } finally {
      setJoining(false);
    }
  };

  const savePicks = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/daily-sim/picks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ picks }),
      });
      const d = await res.json();
      if (!res.ok) { setSaveMsg(d.error); return; }
      setSaveMsg('✅ Picks saved!');
      setSavedOnce(true);
      isDirtyRef.current = false;
      setPicksChanged(false);
      initialPicksRef.current = JSON.stringify(picks);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const adminRun = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/api/daily-sim/admin/run-now`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json();
    alert(d.message || d.error);
    fetchData(); fetchHistory();
  };

  const adminClose = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/api/daily-sim/admin/close-now`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json();
    alert(d.message || d.error);
    fetchData();
  };

  // ── Derived values ────────────────────────────────────────────────────────────

  if (loading) return <div style={S.center}>Loading…</div>;
  if (loadErr) return <div style={{ ...S.center, color: '#dc3545' }}>{loadErr}</div>;

  const { session, entries, teamsById, userJoined } = data;

  const champion = session.simResults ? teamsById[session.simResults.final] : null;
  const third    = session.simResults ? teamsById[session.simResults['3rd_place']] : null;
  const realEntries = entries.filter(e => !e.is_mock).length;

  const groupsComplete = GROUPS.every(g => (picks.groups[g] || []).length === 4);
  const thirdComplete  = (picks.thirdPlaceAdvance || []).length === 8;
  const r32Count       = Object.keys(picks.knockout).filter(k => k.startsWith('r32')).length;
  const locked         = session.status !== 'open';

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      {/* Page header */}
      <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
        <h1 style={S.title}>🎲 Daily Simulation</h1>
        <p style={S.subtitle}>{fmtDate(session.simDate)}</p>
      </div>

      {/* Status card */}
      <StatusCard session={session} tick={tick} />

      {/* Admin controls */}
      {user?.is_admin && session.status !== 'complete' && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '0.75rem' }}>
          {session.status === 'open' && (
            <button onClick={adminClose} style={S.adminBtn('#6c757d')}>🔒 Close Registration</button>
          )}
          <button onClick={adminRun} style={S.adminBtn('#dc3545')}>▶ Run Now</button>
        </div>
      )}

      {/* ── OPEN: bracket picker or join CTA ─────────────────────────────────── */}
      {session.status === 'open' && (
        <>
          {!user ? (
            <div style={{ ...S.card, textAlign: 'center', padding: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '1rem' }}>
                <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600 }}>Log in</Link>
                {' '}to join today's simulation and make your picks!
              </p>
            </div>
          ) : !userJoined ? (
            /* Not yet joined — show join CTA */
            <div style={{ ...S.card, textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: '1.25rem', lineHeight: 1.6 }}>
                Join today's sim and pick your own bracket! Your picks are auto-filled
                with a random bracket to start — edit as much as you like before
                9:00 PM ET, then the sim runs at 9:05 PM ET.
              </p>
              <button onClick={join} disabled={joining} style={S.joinBtn}>
                {joining ? 'Joining…' : '⚽ Join & Pick My Bracket'}
              </button>
              {joinMsg && (
                <p style={{ marginTop: '0.75rem', color: joinMsg.includes('error') || joinMsg.includes('closed') ? '#dc3545' : '#28a745', fontWeight: 500 }}>
                  {joinMsg}
                </p>
              )}
            </div>
          ) : (
            /* Joined — show bracket picker */
            <div style={S.card}>
              <div style={S.cardHeader}>
                📋 Your Bracket Picks
                <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                  Locks at 9:00 PM ET
                </span>
              </div>

              {joinMsg && (
                <div style={{ padding: '0.6rem 1rem', background: '#d4edda', color: '#155724', fontSize: '0.9rem', borderBottom: '1px solid var(--border)' }}>
                  🎲 {joinMsg}
                </div>
              )}

              {/* Scroll hint */}
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'right', margin: '0.3rem 0.75rem 0' }}>scroll →</p>

              {/* Bracket tabs */}
              <div style={S.tabBar}>
                {[
                  ['groups',  `Group Stage ${groupsComplete ? '✓' : ''}`],
                  ['third',   `3rd Place (${(picks.thirdPlaceAdvance||[]).length}/8)`],
                  ['knockout',`Knockout ${r32Count > 0 ? `(${r32Count}/16 R32)` : ''}`],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setBracketTab(key)}
                    style={{ ...S.tab, ...(bracketTab === key ? S.tabActive : {}) }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div style={{ padding: '1rem' }}>
                {bracketTab === 'groups' && (
                  <GroupStage
                    groups={GROUPS}
                    teamsByGroup={teamsByGroupFn}
                    picks={picks.groups}
                    onUpdate={updateGroupOrder}
                    locked={false}
                  />
                )}
                {bracketTab === 'third' && (
                  <ThirdPlacePicker
                    groups={GROUPS}
                    picks={picks}
                    teamsByGroup={teamsByGroupFn}
                    selected={picks.thirdPlaceAdvance || []}
                    onToggle={toggleThirdPlace}
                    locked={false}
                  />
                )}
                {bracketTab === 'knockout' && (
                  <KnockoutBracket
                    picks={picks}
                    teams={teamsArray}
                    knockout={picks.knockout || {}}
                    onPick={updateKnockout}
                    locked={false}
                  />
                )}
              </div>

              {/* Save bar */}
              <div style={S.saveBar}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  <strong>Groups:</strong> {groupsComplete ? '✓' : `in progress`} &nbsp;·&nbsp;
                  <strong>3rd Place:</strong> {(picks.thirdPlaceAdvance||[]).length}/8 &nbsp;·&nbsp;
                  <strong>R32:</strong> {r32Count}/16
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {saveMsg && (
                    <span style={{ fontSize: '0.85rem', color: saveMsg.startsWith('✅') ? '#28a745' : '#dc3545', fontWeight: 500 }}>
                      {saveMsg}
                    </span>
                  )}
                  {picksChanged && !saveMsg && (
                    <span style={{ fontSize: '0.8rem', color: '#fd7e14' }}>Unsaved changes</span>
                  )}
                  <button onClick={savePicks} disabled={saving} style={S.saveBtn}>
                    {saving ? 'Saving…' : '💾 Save Picks'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── CLOSED: waiting ──────────────────────────────────────────────────── */}
      {session.status === 'closed' && (
        <div style={{ ...S.card, padding: '1.25rem', textAlign: 'center', borderLeft: '4px solid #fd7e14' }}>
          <p style={{ margin: 0, fontWeight: 600 }}>
            ⏳ Registration closed — simulation runs at 9:05 PM ET!
          </p>
          {userJoined && (
            <p style={{ margin: '0.5rem 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              You're entered. Good luck! 🍀
            </p>
          )}
        </div>
      )}

      {/* ── COMPLETE: champion + leaderboard ─────────────────────────────────── */}
      {session.status === 'complete' && (
        <>
          {champion && (
            <div style={S.championBanner}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, opacity: 0.85, marginBottom: 4 }}>
                🏆 TODAY'S SIMULATED WORLD CUP CHAMPION
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{champion.name}</div>
              {third && (
                <div style={{ fontSize: '0.8rem', marginTop: 6, opacity: 0.75 }}>
                  🥉 Third place: {third.name}
                </div>
              )}
              <div style={{ fontSize: '0.8rem', marginTop: 6, opacity: 0.75 }}>
                Ran at {fmtTime(session.ranAt)}
              </div>
            </div>
          )}

          {userJoined && data.userEntry && (
            <div style={{ ...S.card, padding: '0.85rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Your result</span>
              <span>
                Rank <strong>{data.userEntry.rank}</strong>
                {MEDAL[data.userEntry.rank] && ` ${MEDAL[data.userEntry.rank]}`}
                &nbsp;·&nbsp;<strong>{data.userEntry.score} pts</strong>
              </span>
            </div>
          )}
        </>
      )}

      {/* ── Leaderboard / player list ─────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          {session.status === 'complete'
            ? '🏅 Leaderboard'
            : `👥 Signed Up (${realEntries} player${realEntries !== 1 ? 's' : ''})`}
        </div>
        {entries.length === 0 ? (
          <p style={{ padding: '1rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            No players yet — be the first!
          </p>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                {session.status === 'complete' && <th style={S.th}>#</th>}
                <th style={S.th}>Player</th>
                {session.status === 'complete' && <th style={{ ...S.th, textAlign: 'right' }}>Score</th>}
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const isMe = user && e.username === user.username;
                return (
                  <tr key={e.id} style={{ background: isMe ? 'rgba(99,102,241,0.07)' : 'transparent' }}>
                    {session.status === 'complete' && (
                      <td style={{ ...S.td, fontWeight: 700, width: '2.5rem' }}>
                        {MEDAL[e.rank] || e.rank}
                      </td>
                    )}
                    <td style={S.td}>
                      <span style={{ fontWeight: isMe ? 700 : 400 }}>{e.display_name}</span>
                      {e.is_mock && <span style={S.badge('#6c757d')}>🤖 bot</span>}
                      {isMe && <span style={S.badge('#6366f1')}>you</span>}
                    </td>
                    {session.status === 'complete' && (
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 700, color: e.rank === 1 ? '#f59e0b' : 'inherit' }}>
                        {e.score} pts
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── History ──────────────────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div style={S.card}>
          <button onClick={() => setShowHistory(h => !h)} style={S.toggleBtn}>
            📅 Past Results {showHistory ? '▲' : '▼'}
          </button>
          {showHistory && (
            <table style={{ ...S.table, marginTop: 0 }}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Winner</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Score</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Players</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={S.td}>{fmtDate(h.sim_date)}</td>
                    <td style={S.td}>
                      {h.winner_name}
                      {h.winner_is_mock && <span style={S.badge('#6c757d')}>🤖</span>}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{h.winner_score} pts</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>{h.entry_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── How it works ─────────────────────────────────────────────────────── */}
      <div style={{ ...S.card, background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div style={S.cardHeader}>ℹ️ How It Works</div>
        <ul style={{ margin: '0.5rem 0 0.75rem', padding: '0 0 0 1.25rem', lineHeight: 1.9, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          <li>Sign up before <strong>9:00 PM ET</strong> and fill out your own bracket picks.</li>
          <li>You start with <strong>random picks</strong> pre-filled — edit as many as you want!</li>
          <li>At <strong>9:05 PM ET</strong>, the entire WC2026 tournament is simulated randomly.</li>
          <li>Your score is based on how well your picks match the simulated results.</li>
          <li>If fewer than 10 players sign up, <strong>bot players</strong> fill the remaining spots.</li>
          <li>A new simulation runs <strong>every day</strong>!</li>
        </ul>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusCard({ session, tick }) {
  const cfg = {
    open:     { color: '#28a745', label: 'Open',     icon: '🟢' },
    closed:   { color: '#fd7e14', label: 'Closed',   icon: '🟡' },
    running:  { color: '#007bff', label: 'Running',  icon: '⚙️' },
    complete: { color: '#6c757d', label: 'Complete', icon: '✅' },
  }[session.status] || { color: '#6c757d', label: session.status, icon: '•' };

  const countdown = session.status === 'open'   ? fmtCountdown(21, 0)
                  : session.status === 'closed'  ? fmtCountdown(21, 29)
                  : null;
  const cdLabel   = session.status === 'open'   ? 'Picks close in'
                  : session.status === 'closed'  ? 'Simulation runs in'
                  : null;

  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${cfg.color}`, marginBottom: '1rem', padding: '0.9rem 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <span style={{ fontWeight: 700 }}>
          {cfg.icon} Status: <span style={{ color: cfg.color }}>{cfg.label}</span>
        </span>
        {countdown && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{cdLabel}</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: cfg.color }}>
              {countdown}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:    { maxWidth: '760px', margin: '0 auto', padding: '1.5rem 1rem' },
  title:   { fontSize: '1.8rem', fontWeight: 800, margin: 0 },
  subtitle:{ color: 'var(--text-muted)', margin: '0.25rem 0 0', fontSize: '0.95rem' },
  center:  { textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' },
  card: {
    background: 'var(--surface)',
    borderRadius: '10px',
    border: '1px solid var(--border)',
    marginBottom: '1rem',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.75rem 1rem',
    fontWeight: 700,
    borderBottom: '1px solid var(--border)',
    fontSize: '0.95rem',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '0.6rem 1rem',
    textAlign: 'left',
    fontSize: '0.78rem',
    color: 'var(--text-muted)',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--border)',
  },
  td: {
    padding: '0.65rem 1rem',
    borderBottom: '1px solid var(--border)',
    fontSize: '0.95rem',
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid var(--border)',
    overflowX: 'auto',
  },
  tab: {
    padding: '0.65rem 1.1rem',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '0.88rem',
    fontWeight: 500,
    color: '#39ff14',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    color: '#39ff14',
    borderBottomColor: '#39ff14',
    fontWeight: 700,
  },
  saveBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.5rem',
    padding: '0.85rem 1rem',
    borderTop: '1px solid var(--border)',
    background: 'var(--surface)',
  },
  saveBtn: {
    padding: '0.55rem 1.25rem',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: '7px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.9rem',
  },
  joinBtn: {
    padding: '0.8rem 2.25rem',
    fontSize: '1.05rem',
    background: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  championBanner: {
    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    color: '#fff',
    borderRadius: '12px',
    padding: '1.25rem',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  toggleBtn: {
    width: '100%',
    textAlign: 'left',
    padding: '0.75rem 1rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '0.95rem',
    color: 'inherit',
  },
  badge: (color) => ({
    marginLeft: 6,
    fontSize: '0.7rem',
    background: `${color}22`,
    color,
    borderRadius: 4,
    padding: '1px 5px',
    verticalAlign: 'middle',
    fontWeight: 700,
  }),
  adminBtn: (bg) => ({
    padding: '0.4rem 1rem',
    background: bg,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
  }),
};
